import en from "@/locales/en.json"
import es from "@/locales/es.json"

type Locale = "es" | "en"
type TranslationNode = string | number | boolean | null | TranslationTree
type TranslationTree = { [key: string]: TranslationNode }
type TranslateOptions = Record<string, string | number | boolean | null | undefined>

type I18nConfig = {
  locale?: string
  defaultLocale?: string
}

const translations: Record<Locale, TranslationTree> = { en, es }
const supportedLocales = Object.keys(translations) as Locale[]

let currentLocale: Locale = "es"
let defaultLocale: Locale = "es"

export function configureI18n(config: I18nConfig = {}) {
  currentLocale = normalizeLocale(config.locale) ?? currentLocale
  defaultLocale = normalizeLocale(config.defaultLocale) ?? defaultLocale
}

export function locale() {
  return currentLocale
}

export function defaultLocaleCode() {
  return defaultLocale
}

export function availableLocales() {
  return supportedLocales
}

export function intlLocale() {
  return currentLocale === "es" ? "es-MX" : "en-US"
}

export function t(key: string, options: TranslateOptions = {}) {
  const value = lookup(currentLocale, key) ?? lookup(defaultLocale, key)

  if (typeof value === "string") {
    return interpolate(value, options)
  }

  if (isTranslationTree(value) && options.count !== undefined) {
    const pluralKey = Number(options.count) === 1 ? "one" : "other"
    const pluralValue = value[pluralKey]

    if (typeof pluralValue === "string") {
      return interpolate(pluralValue, options)
    }
  }

  return key
}

function normalizeLocale(value?: string): Locale | undefined {
  if (!value) return undefined

  const normalized = value.split("-")[0]
  return supportedLocales.includes(normalized as Locale) ? (normalized as Locale) : undefined
}

function lookup(targetLocale: Locale, key: string): TranslationNode | undefined {
  return key.split(".").reduce<TranslationNode | undefined>((node, segment) => {
    if (!isTranslationTree(node)) return undefined
    return node[segment]
  }, translations[targetLocale])
}

function isTranslationTree(value: TranslationNode | undefined): value is TranslationTree {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function interpolate(value: string, options: TranslateOptions) {
  return value.replace(/%\{([^}]+)\}/g, (match, key: string) => {
    const replacement = options[key]
    return replacement === undefined || replacement === null ? match : String(replacement)
  })
}
